import type React from "react";
import { useTranslation } from "react-i18next";

import { textStyles } from "@/lib/textStyles";
import { cn } from "@/lib/utils";
import { STATUS_CONFIG } from "@/features/patients/infrastructure/data/PatientDetailsStatus";

type StatusTone = "success" | "warning" | "error";

interface AssessmentStatusPillProps {
  status?: string | null;
  label?: string;
  tone?: StatusTone;
  className?: string;
  /** Why the stage was cancelled or dropped, shown beside the pill. */
  reason?: string | null;
}

const TONE_CLASS_NAMES: Record<StatusTone, string> = {
  success: "bg-[#E6F4EA] text-[#34A853]",
  warning: "bg-[#FFF4E5] text-[#E37400]",
  error: "bg-[#FDECEC] text-[#C62828]",
};

export const AssessmentStatusPill: React.FC<AssessmentStatusPillProps> = ({
  status,
  label,
  tone = "warning",
  className,
  reason,
}) => {
  const { t } = useTranslation("patients");
  const statusConfig = status ? STATUS_CONFIG[status] : undefined;
  const pillLabel = statusConfig?.text ?? label ?? "Unknown Status";
  const toneClassName = statusConfig?.className ?? TONE_CLASS_NAMES[tone];

  const pill = (
    <span
      className={cn(
        "inline-flex w-fit max-w-full items-center justify-center rounded-lg px-2 py-[5px] text-center",
        toneClassName,
        !reason && className,
      )}
    >
      <span className="break-words" style={textStyles.body}>
        {pillLabel}
      </span>
    </span>
  );

  if (!reason) return pill;

  return (
    <span
      className={cn(
        "inline-flex max-w-full flex-wrap items-center gap-x-2 gap-y-1",
        className,
      )}
    >
      {pill}
      <span
        className="break-words"
        style={{ ...textStyles.body, color: "#5F646F" }}
      >
        {t("patientDetails.statusReason", { reason })}
      </span>
    </span>
  );
};
